export type GameStatus = 'idle' | 'playing' | 'paused' | 'ended';

export interface GameState {
  status: GameStatus;
  score: number;
  placedCount: number;
  totalParts: number;
  missesCount: number;
  level: number; // 0 = without uKonn-X, 1+ = with uKonn-X
  speedStage: number;
  isPaused: boolean;
}

export interface PartDefinition {
  id: string;
  label: string;
  type: 'normal' | 'trash_item';
  image: string;
  slotImage?: string;
  partSize: { w: number; h: number };
  slotSize: { w: number; h: number };
  slotPos: { x: number; y: number }; // percentage
}

export interface FallingPart {
  id: string;
  definition: PartDefinition;
  x: number;
  y: number;
  width: number;
  height: number;
  isDragging: boolean;
  isPlaced: boolean;
  spawnTime: number;
}

export interface Slot {
  id: string;
  acceptId: string;
  x: number;
  y: number;
  width: number;
  height: number;
  isFilled: boolean;
  isHighlighted: boolean;
  isAnimating?: boolean; // Success animation after drop
}

export interface GameConfig {
  spawnIntervalMs: number;
  fallSpeedPxPerSec: number;
  partTimeFailMs: number;
  maxAllowedMisses: number;
  maxOnScreen: number;
  level0DurationMs: number;
  // Design dimensions for responsive scaling
  designWidth: number;
  designHeight: number;
}

export const GAME_CONFIG: GameConfig = {
  spawnIntervalMs: 2000,
  fallSpeedPxPerSec: 90, // Base value in design pixels, will be scaled
  partTimeFailMs: 15000,
  maxAllowedMisses: 5,
  maxOnScreen: 4,
  level0DurationMs: 5000,
  designWidth: 1280,
  designHeight: 850,
};

export const PART_DEFINITIONS: PartDefinition[] = [
  {
    id: 'PULT',
    label: 'Pult',
    type: 'normal',
    image: '/assets/automation-drop/pult_part.png',
    slotImage: '/assets/automation-drop/platzhalter_hoch.png',
    partSize: { w: 180, h: 290 },
    slotSize: { w: 90, h: 120 },
    slotPos: { x: 13.3, y: 60.2 },
  },
  {
    id: 'BUSMODUL_1',
    label: 'Busmodul 1',
    type: 'normal',
    image: '/assets/automation-drop/impact.png',
    slotImage: '/assets/automation-drop/platzhalter_breit.png',
    partSize: { w: 70, h: 30 },
    slotSize: { w: 110, h: 40 },
    slotPos: { x: 30.0, y: 72.9 },
  },
  {
    id: 'ROBOTER',
    label: 'Roboter',
    type: 'normal',
    image: '/assets/automation-drop/roboterzelle.png',
    slotImage: '/assets/automation-drop/platzhalter_hoch.png',
    partSize: { w: 230, h: 230 },
    slotSize: { w: 90, h: 90 },
    slotPos: { x: 39.8, y: 54.3 },
  },
  {
    id: 'NOTAUS',
    label: 'Notaus',
    type: 'normal',
    image: '/assets/automation-drop/safetybutton.png',
    slotImage: '/assets/automation-drop/platzhalter_breit.png',
    partSize: { w: 75, h: 25 },
    slotSize: { w: 100, h: 35 },
    slotPos: { x: 59.1, y: 73.8 },
  },
  {
    id: 'BUSMODUL_3',
    label: 'Busmodul 3',
    type: 'normal',
    image: '/assets/automation-drop/impact3.png',
    slotImage: '/assets/automation-drop/platzhalter_breit.png',
    partSize: { w: 80, h: 35 },
    slotSize: { w: 110, h: 40 },
    slotPos: { x: 69.7, y: 66.3 },
  },
  {
    id: 'VARIO_X',
    label: 'Vario-X',
    type: 'normal',
    image: '/assets/automation-drop/varioX.png',
    slotImage: '/assets/automation-drop/platzhalter_breit.png',
    partSize: { w: 150, h: 60 },
    slotSize: { w: 120, h: 45 },
    slotPos: { x: 85.2, y: 40.0 },
  },
  {
    id: 'ANTRIEB',
    label: 'Antrieb',
    type: 'normal',
    image: '/assets/automation-drop/motor.png',
    slotImage: '/assets/automation-drop/platzhalter_breit.png',
    partSize: { w: 70, h: 25 },
    slotSize: { w: 110, h: 40 },
    slotPos: { x: 52.3, y: 28.1 },
  },
  {
    id: 'BUSMODUL_2',
    label: 'Busmodul 2',
    type: 'normal',
    image: '/assets/automation-drop/impact2.png',
    slotImage: '/assets/automation-drop/platzhalter_breit.png',
    partSize: { w: 70, h: 30 },
    slotSize: { w: 110, h: 40 },
    slotPos: { x: 65.5, y: 26.4 },
  },
  {
    id: 'LICHTSCHRANKE_1',
    label: 'Lichtschranke 1',
    type: 'normal',
    image: '/assets/automation-drop/Lichtschrankelinks.png',
    slotImage: '/assets/automation-drop/platzhalter_hoch.png',
    partSize: { w: 40, h: 100 },
    slotSize: { w: 45, h: 110 },
    slotPos: { x: 68.8, y: 35.6 },
  },
  {
    id: 'LICHTSCHRANKE_2',
    label: 'Lichtschranke 2',
    type: 'normal',
    image: '/assets/automation-drop/Lichtschrankelinks.png',
    slotImage: '/assets/automation-drop/platzhalter_hoch.png',
    partSize: { w: 40, h: 120 },
    slotSize: { w: 45, h: 130 },
    slotPos: { x: 82.0, y: 36.2 },
  },
  // Safety Kabelbäume (Verbindungskabel)
  {
    id: 'KABELBAUM_SAFETY1',
    label: 'Kabelbaum Safety 1',
    type: 'normal',
    image: '/assets/automation-drop/Safety1.png',
    slotImage: '/assets/automation-drop/platzhalter_hoch.png',
    partSize: { w: 500, h: 500 },
    slotSize: { w: 400, h: 400 },
    slotPos: { x: 60.0, y: 19.4 },
  },
  {
    id: 'KABELBAUM_SAFETY2',
    label: 'Kabelbaum Safety 2',
    type: 'normal',
    image: '/assets/automation-drop/Safety2.png',
    slotImage: '/assets/automation-drop/platzhalter_hoch.png',
    partSize: { w: 500, h: 500 },
    slotSize: { w: 400, h: 400 },
    slotPos: { x: 74.0, y: 40.6 },
  },
  // Trash Item (Alter Schaltschrank)
  {
    id: 'SCHALTSCHRANK',
    label: 'Alter Schaltschrank',
    type: 'trash_item',
    image: '/assets/automation-drop/schaltschrank1.png',
    partSize: { w: 90, h: 140 },
    slotSize: { w: 0, h: 0 },
    slotPos: { x: 0, y: 0 },
  },
];

// Translations moved to /src/i18n/
